import { config } from "@/config";
import { parseAgentEvent } from "@/core/agent-event";
import type { AgentRunSummary, ChatMessage, ClassifyProposeResponse, CompetitorOp, Disposition, Draft, EditOp, ResearchProposeResponse, ResearchTarget, RevertResponse, SessionSummary, StepCode, StructureContent, ThresholdsProposeResponse, ToolEvent, ValidationReceipt, Version, WorkflowSession } from "@/domain/types";
import { callFn, db, streamFn } from "@/infra/cloud-adapter/client";
import { AGENT_REPLY_UNFINISHED_TEXT, AGENT_TURN_TIMEOUT_TEXT, REVIEW_TIMEOUT_TEXT } from "@/lib/labels";

/**
 * Calls a server operation, and gives up on it after `timeoutMs` with `timeoutText`, so a hung connection never leaves its caller (and the
 * draft's work lock the caller holds) waiting for ever. Any other failure passes on unchanged.
 */
async function callWithTimeout<T>(name: string, body: unknown, timeoutMs: number, timeoutText: string): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await callFn<T>(name, body, controller.signal);
  } catch (e) {
    if (controller.signal.aborted) throw new Error(timeoutText);
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/** An agent run: as long as a turn may take. */
const callAgent = <T>(name: string, body: unknown) => callWithTimeout<T>(name, body, config.agentTurnTimeoutMs, AGENT_TURN_TIMEOUT_TEXT);
/** A save, a Keep, Remove or Undo: each holds the draft's lock while it runs. */
const callReview = <T>(name: string, body: unknown) => callWithTimeout<T>(name, body, config.reviewTimeoutMs, REVIEW_TIMEOUT_TEXT);

export async function openSessions(tenantId: string): Promise<SessionSummary[]> {
  const { data, error } = await db().from("workflow_sessions").select("id,draft_id,current_step,updated_at").eq("tenant_id", tenantId).eq("status", "open").order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as SessionSummary[];
}
export async function getSession(id: string): Promise<WorkflowSession | null> {
  const { data, error } = await db().from("workflow_sessions").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return (data as unknown as WorkflowSession) ?? null;
}
/** Every open Guided Packaging session in the workspace, newest first: one per structure document being authored. Light rows; `getSession` reads one in full. */
export async function listOpenSessions(workspaceId: string): Promise<SessionSummary[]> {
  const { data, error } = await db().from("workflow_sessions").select("id,draft_id,current_step,updated_at").eq("workspace_id", workspaceId).eq("workflow_code", "guided_packaging").eq("status", "open").order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as SessionSummary[];
}
/** Every draft still in authoring or awaiting approval in the workspace, newest first. */
export async function openDrafts(workspaceId: string): Promise<Draft[]> {
  const { data, error } = await db().from("drafts").select("*").eq("workspace_id", workspaceId).in("status", ["open", "submitted"]).order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Draft[];
}
/**
 * Where a version shown view only came from: the draft it was published from (a draft and its version are written in one
 * transaction, so they carry the same time) and that draft's latest session, with its research, checks and step progress.
 * A draft shown is its own source. None for a version with no draft on record (the sample).
 */
export async function versionSource(workspaceId: string, from: { draft?: Draft; version?: Version }): Promise<{ draft: Draft | null; session: WorkflowSession | null }> {
  let draft = from.draft ?? null;
  if (!draft && from.version) {
    const { data, error } = await db().from("drafts").select("id,workspace_id,base_version_id,title,status,updated_at,version_label,source_page")
      .eq("workspace_id", workspaceId).eq("status", "published");
    if (error) throw error;
    const at = Date.parse(from.version.published_at);
    const found = ((data ?? []) as unknown as Draft[]).find((d) => d.updated_at === from.version!.published_at || Date.parse(d.updated_at) === at);
    draft = found ? { ...found, content: from.version.content } : null;
  }
  if (!draft) return { draft: null, session: null };
  const { data, error } = await db().from("workflow_sessions").select("*").eq("draft_id", draft.id).order("created_at", { ascending: false }).limit(1).maybeSingle();
  if (error) throw error;
  return { draft, session: (data as unknown as WorkflowSession) ?? null };
}
export async function agentRuns(sessionId: string): Promise<AgentRunSummary[]> {
  const { data, error } = await db().from("agent_runs").select("*").eq("session_id", sessionId).order("created_at");
  if (error) throw error;
  return (data ?? []) as AgentRunSummary[];
}

export const setStep = (session_id: string, current_step: StepCode, state?: Record<string, unknown>) => callFn<{ session: WorkflowSession }>("session-update", { session_id, current_step, state });
/** Records a download of the Good, Better, Best deliverable in the audit trail; the file itself is built in the browser and never stored. */
export const deliverableDownloaded = (body: { draft_id: string } | { version_id: string }, format: DeliverableFormat) =>
  callFn<{ ok: boolean }>("deliverable-downloaded", { ...body, format });
export type DeliverableFormat = "html" | "png" | "xlsx";
/** One page of "Recommendations for low-value features", listed and paged by the server from the draft. */
export interface KillerPage { total: number; page: number; page_size: number; pages: number; feature_ids: string[] }
export const killerRecommendations = (draft_id: string, page: number, page_size: number) => callFn<KillerPage>("killer-recommendations", { draft_id, page, page_size });
/** Oxley Assistant rates the features that have no rating and writes the ratings into the draft, marked. `run_id` is the run Undo these changes reverts. */
export const classifyPropose = (session_id: string) => callAgent<ClassifyProposeResponse>("classify-propose", { session_id });
/** Oxley Assistant drafts a value threshold statement and anchor metric for editions without one and writes them into the draft, marked. */
export const thresholdsPropose = (session_id: string) => callAgent<ThresholdsProposeResponse>("thresholds-propose", { session_id });
/** One assistant turn researches the product's market (about a minute), writes value drivers into the draft, marked, and posts the request and the reply in the chat. With no competitors, the assistant finds them. */
export const researchPropose = (session_id: string, input: { customer_site: string; competitors?: string[] }) =>
  callAgent<ResearchProposeResponse>("research-propose", { session_id, ...input });
/** Changes the Research checklist: add a website (it arrives ticked), tick or untick a row, or remove it. The server checks the address and the limit, and refuses with its reason. */
export const updateCompetitors = (session_id: string, op: CompetitorOp, website: string) =>
  callFn<{ session: WorkflowSession }>("competitors-update", { session_id, op, website });
/**
 * The person's own write: one change, or several that belong together (an option set, an Ask suggestion), saved straight into the open draft.
 * The server drops the Oxley Assistant mark of every value it touches, so a value a person writes is the person's.
 */
export const editDraft = (draft_id: string, summary: string, edit: EditOp | EditOp[]) => callReview<{ committed: number }>("draft-edit", { draft_id, summary, patch: Array.isArray(edit) ? edit : [edit] });
/** Keep drops the mark and keeps the value; Remove puts the earlier value back and drops the mark (the answer lists what it put back). `targets` is the list of marked targets, or "all". */
export const markAction = (draft_id: string, action: "keep" | "remove", targets: string[] | "all") => callReview<RevertResponse>("assistant-marks", { draft_id, action, targets });
/** Reverts what one assistant run wrote, except the values a person has edited since. The answer lists what was put back. */
export const undoAssistantRun = (draft_id: string, run_id: string) => callReview<RevertResponse>("assistant-undo", { draft_id, run_id });
/** Records the decision on a Killer. A null disposition clears it. A segment add-on needs at least one segment (the server refuses otherwise). */
export const dispose = (draft_id: string, feature_id: string, disposition: Disposition | null, extra: { segments?: string[] } = {}) =>
  callFn<{ decision_record: unknown; content: StructureContent }>("dispose", { draft_id, feature_id, disposition, ...extra });
export const validateDraft = (session_id: string) => callFn<{ receipt: ValidationReceipt; previous: ValidationReceipt | null }>("validate-draft", { session_id });
/** Publishes the session's draft as the next version of its product. No approval: the draft and its session close, and the draft is locked for good. */
export const publishDraft = (session_id: string) => callFn<{ status: "published"; version: Version; receipt: ValidationReceipt; assistant_values: number }>("publish", { session_id });
export const validateStructure = (tenant_id: string, draft_id: string | null, structure: unknown) => callFn<ValidationReceipt>("kernel-validate", { tenant_id, draft_id, structure });

/** What starts an agent turn: a message the person typed, or a research request (the server writes the request into the chat itself). */
export type AgentTurnInput = { message: string } | { research_target: ResearchTarget };

/**
 * Runs one agent turn. The streamed text (`delta`) is only a preview: the saved reply is the message of `done`, which the server has
 * cleaned, and an `error` replaces whatever streamed. A turn that gets no end within `config.agentTurnTimeoutMs` is given up on and
 * fails with AGENT_TURN_TIMEOUT_TEXT, so a hung connection never leaves the caller waiting for ever. A stream that closes with neither a
 * `done` nor an `error` fails with AGENT_REPLY_UNFINISHED_TEXT.
 */
export async function agentTurn(session_id: string, input: AgentTurnInput, on: {
  user: (m: ChatMessage) => void;
  /** `model` is the model the organization chose for the step; a server that sends none gives undefined. */
  status: (tier: string, model?: string) => void;
  delta: (t: string) => void;
  tool: (ev: ToolEvent) => void;
  done: (m: ChatMessage) => void;
  error: (m: ChatMessage) => void;
}, signal?: AbortSignal) {
  const controller = new AbortController();
  // Propagate an external stop signal (user clicked Stop) into the internal controller.
  signal?.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  const timer = setTimeout(() => controller.abort(), config.agentTurnTimeoutMs);
  let ended = false;
  try {
    await streamFn("agent-turn", { session_id, ...input }, (raw) => {
      // Discard backend events that arrive after the user stopped — they race the abort signal.
      if (signal?.aborted) return;
      const ev = parseAgentEvent(raw);
      if (!ev) return;
      if (ev.type === "user") on.user(ev.message);
      else if (ev.type === "status") on.status(ev.tier, ev.model);
      else if (ev.type === "delta") on.delta(ev.text);
      else if (ev.type === "tool") on.tool(ev);
      else if (ev.type === "done") { ended = true; on.done(ev.message); }
      else if (ev.type === "error") { ended = true; on.error(ev.message); }
    }, controller.signal);
    // A connection that is cut closes the stream cleanly: without a done or an error there is no reply, only a preview of one.
    // When the user stopped deliberately, treat that clean close as success (no "unfinished" error).
    if (!ended && !signal?.aborted) throw new Error(AGENT_REPLY_UNFINISHED_TEXT);
  } catch (e) {
    // Only replace the error with the timeout message when the *timeout* fired, not when the user stopped.
    if (controller.signal.aborted && !signal?.aborted) throw new Error(AGENT_TURN_TIMEOUT_TEXT);
    // User stopped — resolve cleanly so the caller's finally block runs without entering catch.
    if (signal?.aborted) return;
    throw e;
  } finally {
    clearTimeout(timer);
  }
}
